import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, Copy, ArrowLeft, Triangle, Sparkles, ExternalLink, Youtube, Loader2, Search as SearchIcon, Clock, Users, ChevronLeft, ChevronRight, Library, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PHASE_MAP, AREA_MAP } from "@/lib/phases";
import { SitePlaceholder } from "@/components/SitePlaceholder";
import { SiteMiniMap } from "@/components/SiteMiniMap";
import { FavoriteControls } from "@/components/FavoriteControls";
import { NearbySuggestions } from "@/components/NearbySuggestions";
import { ReasonDialog } from "@/components/ReasonDialog";

const Spec = ({ label, value, badge }) => (
  <div className="py-2 border-b border-border">
    <dt className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</dt>
    <dd className="mt-0.5 font-serif text-base text-foreground flex items-center gap-2 flex-wrap">
      <span>{value || "—"}</span>
      {value && badge}
    </dd>
  </div>
);

const AiEstimateBadge = () => (
  <span
    data-testid="ai-estimate-badge"
    title="Approximate date estimated by AI from the site's type and location — not a sourced figure."
    className="inline-flex items-center gap-1 border border-accent/40 text-accent font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5"
  >
    <Sparkles className="h-2.5 w-2.5" /> AI estimate
  </span>
);

const commonsInfo = (url) => {
  try {
    const m = url.match(/Special:FilePath\/([^?]+)/);
    if (m) {
      const raw = m[1];
      const name = decodeURIComponent(raw);
      const title = name.replace(/\.[a-z0-9]+$/i, "").replace(/_/g, " ").trim();
      return { title, source: `https://commons.wikimedia.org/wiki/File:${raw}`, isCommons: true };
    }
  } catch (e) { /* noop */ }
  return { title: null, source: url, isCommons: false };
};

export default function PyramidDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [heroBroken, setHeroBroken] = useState(false);
  const [research, setResearch] = useState(null);
  const [rLoading, setRLoading] = useState(false);
  const [rErr, setRErr] = useState("");
  const [adjacent, setAdjacent] = useState({ prev: null, next: null });
  const [sources, setSources] = useState([]);
  const [removeOpen, setRemoveOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setResearch(null);
    setRErr("");
    setAdjacent({ prev: null, next: null });
    setSources([]);
    api.get(`/pyramids/${slug}`).then((r) => {
      setP(r.data);
      setResearch(r.data.research || null);
      api.get(`/pyramids/${slug}/adjacent`, { params: { area: r.data.area } }).then((a) => setAdjacent(a.data)).catch(() => {});
    }).catch(() => setNotFound(true));
    api.get(`/pyramids/${slug}/sources`).then((r) => setSources(r.data || [])).catch(() => {});
  }, [slug]);

  useEffect(() => {
    const onKey = (e) => {
      if (lightbox !== null) return;
      const tag = (e.target.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea") return;
      if (e.key === "ArrowLeft" && adjacent.prev) navigate(`/pyramid/${adjacent.prev.slug}`);
      if (e.key === "ArrowRight" && adjacent.next) navigate(`/pyramid/${adjacent.next.slug}`);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [adjacent, lightbox, navigate]);

  const removeEntry = () => {
    if (!user) { toast.error("Please sign in to reclassify entries"); return; }
    setRemoveOpen(true);
  };

  const doRemove = async (reason) => {
    try {
      await api.delete(`/pyramids/${p.slug}`, { params: { reason } });
      toast.success("Sent to Recycle Bin", { description: p.name });
      navigate("/archive");
    } catch (e) {
      toast.error("Could not remove entry", { description: e.response?.data?.detail || "Please try again." });
    }
  };


  const runResearch = async (force = false) => {
    setRLoading(true);
    setRErr("");
    try {
      const { data } = await api.post(`/pyramids/${slug}/research`, null, { params: force ? { refresh: true } : {} });
      setResearch(data);
      setP((prev) => {
        if (!prev) return prev;
        const added = (data.gallery?.length || 0) - (prev.gallery?.length || 0);
        if (added > 0) toast.success(`Added ${added} new photo${added > 1 ? "s" : ""} to the gallery`);
        const gainedDeepDive = data.theories && !(prev.theories?.length);
        if (gainedDeepDive) toast.success("Deep-dive written — mysteries & context added");
        return {
          ...prev,
          gallery: data.gallery ?? prev.gallery,
          summary: data.summary ?? prev.summary,
          mainstream: data.mainstream ?? prev.mainstream,
          theories: data.theories ?? prev.theories,
          region: data.region ?? prev.region,
          era: data.era ?? prev.era,
          estimated_age: data.estimated_age ?? prev.estimated_age,
          stone_type: data.stone_type ?? prev.stone_type,
        };
      });
    } catch (e) {
      setRErr("Research generation failed. Please try again.");
    } finally {
      setRLoading(false);
    }
  };

  if (notFound)
    return (
      <div className="mx-auto max-w-3xl px-6 py-32 text-center">
        <h1 className="font-serif text-3xl font-bold">Entry not found</h1>
        <Link to="/archive" className="mt-4 inline-block font-mono text-xs uppercase tracking-widest text-accent">← Back to archive</Link>
      </div>
    );

  if (!p) return <div className="px-6 py-32 text-center font-mono text-sm text-muted-foreground">Loading entry…</div>;

  const coords = `${p.lat.toFixed(4)}, ${p.lng.toFixed(4)}`;
  const copyCoords = () => {
    navigator.clipboard.writeText(coords);
    toast.success("Coordinates copied", { description: coords });
  };

  return (
    <div>
      {/* Floating prev/next (desktop) */}
      {adjacent.prev && (
        <button
          data-testid="detail-prev-float"
          onClick={() => navigate(`/pyramid/${adjacent.prev.slug}`)}
          title={`Previous: ${adjacent.prev.name}`}
          className="hidden lg:flex fixed left-3 top-1/2 -translate-y-1/2 z-[900] items-center justify-center h-12 w-12 rounded-full border border-border bg-background/80 backdrop-blur text-muted-foreground shadow-lg hover:text-foreground hover:border-accent hover:scale-105 transition-all"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
      )}
      {adjacent.next && (
        <button
          data-testid="detail-next-float"
          onClick={() => navigate(`/pyramid/${adjacent.next.slug}`)}
          title={`Next: ${adjacent.next.name}`}
          className="hidden lg:flex fixed right-3 top-1/2 -translate-y-1/2 z-[900] items-center justify-center h-12 w-12 rounded-full border border-border bg-background/80 backdrop-blur text-muted-foreground shadow-lg hover:text-foreground hover:border-accent hover:scale-105 transition-all"
        >
          <ChevronRight className="h-6 w-6" />
        </button>
      )}
      {/* Hero */}
      <section className="relative grain">
        <div className="absolute inset-0">
          {p.gallery?.[0] && !heroBroken ? (
            <img src={p.gallery[0]} onError={() => setHeroBroken(true)} alt={p.name} className="h-full w-full object-cover" />
          ) : (
            <SitePlaceholder phase={p.phase} className="h-full w-full" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-background/40" />
        </div>
        <div className="relative mx-auto max-w-6xl px-4 sm:px-8 lg:px-12 pt-4 pb-5">
          <div className="flex items-center justify-between gap-3">
            <Link to="/archive" data-testid="back-to-archive" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="h-3.5 w-3.5" /> Archive Index
            </Link>
            <div className="flex items-center gap-2">
              <button
                data-testid="detail-prev"
                onClick={() => adjacent.prev && navigate(`/pyramid/${adjacent.prev.slug}`)}
                disabled={!adjacent.prev}
                title={adjacent.prev ? `Previous: ${adjacent.prev.name}` : "No previous entry"}
                className="inline-flex items-center gap-1.5 border border-border bg-background/70 backdrop-blur px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground hover:border-foreground/40 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
                <span className="hidden sm:inline max-w-[9rem] truncate">{adjacent.prev ? adjacent.prev.name : "Prev"}</span>
                <span className="sm:hidden">Prev</span>
              </button>
              <button
                data-testid="detail-next"
                onClick={() => adjacent.next && navigate(`/pyramid/${adjacent.next.slug}`)}
                disabled={!adjacent.next}
                title={adjacent.next ? `Next: ${adjacent.next.name}` : "No next entry"}
                className="inline-flex items-center gap-1.5 border border-border bg-background/70 backdrop-blur px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground hover:border-foreground/40 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <span className="sm:hidden">Next</span>
                <span className="hidden sm:inline max-w-[9rem] truncate">{adjacent.next ? adjacent.next.name : "Next"}</span>
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-6xl px-4 sm:px-8 lg:px-12 py-6 grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
        {/* Main column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Title block */}
          <div>
            <span className="bg-foreground/90 text-amber-400 font-mono text-[10px] uppercase tracking-widest px-2.5 py-1">
              {p.country}
            </span>
            <span className="ml-2 text-white font-mono text-[10px] uppercase tracking-widest px-2.5 py-1" style={{ background: (PHASE_MAP[p.phase] || PHASE_MAP.pyramids).color }}>
              {AREA_MAP[p.area]?.label || "Structures"}{(PHASE_MAP[p.phase] || {}).roman ? ` · ${(PHASE_MAP[p.phase]).label}` : ""}
            </span>
            <h1 data-testid="pyramid-detail-title" className="mt-3 font-serif text-3xl sm:text-5xl font-bold text-foreground leading-none">
              {p.name}
            </h1>
            {p.alt_names?.length > 0 && (
              <p className="mt-1.5 font-serif italic text-lg text-muted-foreground">{p.alt_names.join(" · ")}</p>
            )}
            <div className="mt-3 flex flex-wrap items-center gap-4">
              <span className="flex items-center gap-1.5 font-mono text-xs uppercase tracking-wider text-muted-foreground">
                <MapPin className="h-3.5 w-3.5 text-accent" /> {p.region}
              </span>
              <button data-testid="copy-coords" onClick={copyCoords} className="flex items-center gap-1.5 font-mono text-xs text-muted-foreground hover:text-accent transition-colors">
                <Copy className="h-3.5 w-3.5" /> {coords}
              </button>
            </div>
          </div>

          <FavoriteControls slug={p.slug} name={p.name} />

          {user && (
            <button
              data-testid="detail-delete-button"
              onClick={removeEntry}
              className="inline-flex items-center gap-2 border border-destructive/40 text-destructive font-mono text-[11px] uppercase tracking-widest px-4 py-2.5 hover:bg-destructive hover:text-destructive-foreground transition-colors"
            >
              <Trash2 className="h-4 w-4" /> Send to Recycle Bin
            </button>
          )}

          <ReasonDialog
            open={removeOpen}
            onOpenChange={setRemoveOpen}
            title={`Remove “${p.name}”?`}
            description="Pick a reason. It will move to the Recycle Bin and an admin can restore it later."
            onConfirm={doRemove}
          />

          <div>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-accent mb-2">Overview</p>
            <p className="font-serif text-xl leading-relaxed text-foreground">{p.summary}</p>
          </div>

          <div>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-accent mb-2">Mainstream Explanation</p>
            <p className="text-base text-muted-foreground leading-relaxed">{p.mainstream}</p>
          </div>

          {/* Theories */}
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-accent mb-4">Unexplained Mysteries &amp; Theories</p>
            {p.theories?.length > 0 ? (
              <Accordion type="single" collapsible className="border-t border-border" data-testid="theories-accordion">
                {p.theories.map((t, i) => (
                  <AccordionItem key={i} value={`t-${i}`} data-testid={`theory-${i}`}>
                    <AccordionTrigger className="text-left hover:no-underline">
                      <div className="flex items-start gap-3">
                        <Triangle className="h-4 w-4 text-accent mt-1 shrink-0" />
                        <div>
                          <span className="block font-mono text-[10px] uppercase tracking-widest text-accent">{t.category}</span>
                          <span className="font-serif text-xl font-semibold text-foreground">{t.title}</span>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pl-7 text-base text-muted-foreground leading-relaxed">
                      {t.text}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            ) : (
              <div data-testid="theories-empty" className="border border-dashed border-border bg-secondary/40 p-6">
                <p className="text-sm text-muted-foreground leading-relaxed">
                  This entry was catalogued from open archival data. A full analytical deep-dive —
                  documenting its measurements, alignments and open questions — has not yet been written.
                </p>
              </div>
            )}
          </div>

          {/* Gallery moved to full-width section below */}
        </div>

        {/* Specs sidebar (top-right) */}
        <aside className="lg:col-span-1">
          <div className="bg-card border border-border p-6">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-accent mb-3">Dossier · Specifications</p>
            <dl>
              <Spec label="Country" value={p.country} />
              <Spec label="Region" value={p.region} />
              <Spec label="Coordinates" value={coords} />
              <Spec label="Current Height" value={p.height_m ? `${p.height_m} m` : null} />
              <Spec label="Original Height" value={p.original_height_m ? `${p.original_height_m} m` : null} />
              <Spec label="Base Length" value={p.base_length_m ? `${p.base_length_m} m` : null} />
              <Spec label="Era" value={p.era} badge={p.date_source === "ai" ? <AiEstimateBadge /> : null} />
              <Spec label="Estimated Age" value={p.estimated_age} badge={p.date_source === "ai" ? <AiEstimateBadge /> : null} />
              <Spec label="Construction" value={p.stone_type} />
            </dl>
            <a
              href={`https://www.openstreetmap.org/?mlat=${p.lat}&mlon=${p.lng}#map=6/${p.lat}/${p.lng}`}
              target="_blank"
              rel="noreferrer"
              className="mt-5 block text-center bg-accent text-accent-foreground font-mono text-xs uppercase tracking-widest px-4 py-3 hover:bg-accent/90 transition-colors"
            >
              View on OpenStreetMap
            </a>
          </div>
        </aside>
      </div>

      {/* Photo Gallery — full width across both columns */}
      {p.gallery?.length > 0 && (
        <section className="mx-auto max-w-6xl px-4 sm:px-8 lg:px-12 pb-4">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-accent mb-4">Photo Gallery</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="photo-gallery">
            {p.gallery.map((img, i) => {
              const info = commonsInfo(img);
              return (
                <figure key={i} className="space-y-1.5">
                  <button onClick={() => setLightbox(i)} data-testid={`gallery-img-${i}`} className="group relative block aspect-square w-full overflow-hidden bg-secondary border border-border">
                    <img src={img} alt={info.title || `${p.name} ${i + 1}`} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
                  </button>
                  <figcaption data-testid={`gallery-caption-${i}`} className="text-[11px] leading-snug text-muted-foreground line-clamp-2">
                    <span className="align-middle">{info.title || p.name}</span>
                    {info.isCommons && (
                      <a href={info.source} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="ml-1 font-mono text-[9px] uppercase tracking-wider text-accent hover:underline whitespace-nowrap">
                        Commons ↗
                      </a>
                    )}
                  </figcaption>
                </figure>
              );
            })}
          </div>
        </section>
      )}

      {/* AI Research */}
      <section className="mx-auto max-w-6xl px-4 sm:px-8 lg:px-12 pb-14">
        <div className="border border-border bg-card">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 border-b border-border">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-accent flex items-center gap-2">
                <Sparkles className="h-4 w-4" /> AI Research Assistant
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                Generate curated references and videos to keep exploring {p.name}.
              </p>
              {research?.generated_at && (
                <p data-testid="research-last-run" className="mt-1.5 font-mono text-[11px] uppercase tracking-widest text-muted-foreground/80 flex items-center gap-1.5">
                  <Clock className="h-3 w-3 text-accent" />
                  Last researched · {new Date(research.generated_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}
                </p>
              )}
            </div>
            <button
              data-testid="ai-research-btn"
              onClick={() => runResearch(!!research)}
              disabled={rLoading}
              className="flex items-center justify-center gap-2 bg-foreground text-background font-mono text-xs uppercase tracking-widest px-5 py-3 hover:opacity-90 transition-opacity disabled:opacity-60 shrink-0"
            >
              {rLoading ? <><Loader2 className="h-4 w-4 animate-spin" /> Researching…</> :
                research ? <><Sparkles className="h-4 w-4" /> Regenerate</> : <><Sparkles className="h-4 w-4" /> Research this site</>}
            </button>
          </div>

          {rErr && <p data-testid="research-error" className="p-5 font-mono text-xs text-destructive">{rErr}</p>}

          {rLoading && !research && (
            <div className="p-10 text-center font-mono text-sm text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mx-auto mb-3 text-accent" />
              Gathering sources and videos…
            </div>
          )}

          {research && (
            <div data-testid="research-panels" className="p-5 space-y-6">
              {research.overview && (
                <p className="font-serif text-lg leading-relaxed text-foreground border-l-2 border-accent pl-4">{research.overview}</p>
              )}

              {/* Map + Timeline */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <p className="font-mono text-xs uppercase tracking-[0.25em] text-accent mb-3 flex items-center gap-2">
                    <MapPin className="h-3.5 w-3.5" /> Location
                  </p>
                  <SiteMiniMap lat={p.lat} lng={p.lng} color={(PHASE_MAP[p.phase] || PHASE_MAP.pyramids).color} />
                  <p className="mt-2 font-mono text-[11px] text-muted-foreground">{p.region ? `${p.region} · ` : ""}{coords}</p>
                </div>
                <div>
                  <p className="font-mono text-xs uppercase tracking-[0.25em] text-accent mb-3 flex items-center gap-2">
                    <Clock className="h-3.5 w-3.5" /> Timeline
                  </p>
                  {research.timeline?.length > 0 ? (
                    <ol className="relative border-l border-border ml-1.5 space-y-3" data-testid="research-timeline">
                      {research.timeline.map((t, i) => (
                        <li key={i} className="ml-4 relative">
                          <span className="absolute -left-[22px] top-1 h-2.5 w-2.5 rounded-full border-2 border-background" style={{ background: (PHASE_MAP[p.phase] || PHASE_MAP.pyramids).color }} />
                          <span className="font-mono text-[11px] uppercase tracking-wider text-accent">{t.year}</span>
                          <a
                            href={t.url || research.search?.google}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`timeline-link-${i}`}
                            className="group flex items-start gap-1.5 text-sm text-foreground leading-snug hover:text-accent transition-colors"
                          >
                            <span>{t.event}</span>
                            <ExternalLink className="h-3 w-3 mt-1 shrink-0 opacity-0 group-hover:opacity-70 transition-opacity" />
                          </a>
                        </li>
                      ))}
                    </ol>
                  ) : (
                    <p className="font-mono text-xs text-muted-foreground">No timeline available — try Regenerate.</p>
                  )}
                </div>
              </div>

              {/* Experts */}
              {research.experts?.length > 0 && (
                <div>
                  <p className="font-mono text-xs uppercase tracking-[0.25em] text-accent mb-3 flex items-center gap-2">
                    <Users className="h-3.5 w-3.5" /> Leading Researchers
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="research-experts">
                    {research.experts.map((e, i) => (
                      <a key={i} href={e.url} target="_blank" rel="noreferrer"
                        data-testid={`research-expert-${i}`}
                        className="border border-border p-3 flex gap-3 transition-colors hover:border-accent hover:bg-accent/5 group">
                        <span className="flex items-center justify-center h-8 w-8 shrink-0 font-mono text-xs text-background" style={{ background: (PHASE_MAP[p.phase] || PHASE_MAP.pyramids).color }}>
                          {(e.name || "?").trim().charAt(0)}
                        </span>
                        <div className="min-w-0">
                          <p className="font-serif text-base font-semibold text-foreground leading-tight group-hover:text-accent flex items-center gap-1">
                            {e.name}
                            {e.verified === false
                              ? <SearchIcon className="h-3 w-3 shrink-0 opacity-0 group-hover:opacity-70" />
                              : <ExternalLink className="h-3 w-3 shrink-0 opacity-0 group-hover:opacity-70" />}
                          </p>
                          {e.role && <p className="font-mono text-[10px] uppercase tracking-wider text-accent">{e.role}</p>}
                          {e.note && <p className="mt-0.5 text-xs text-muted-foreground leading-snug">{e.note}</p>}
                        </div>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* References */}
                <div>
                  <p className="font-mono text-xs uppercase tracking-[0.25em] text-accent mb-3 flex items-center gap-2">
                    <ExternalLink className="h-3.5 w-3.5" /> Reference Sources
                  </p>
                  <div className="space-y-2" data-testid="research-references">
                    {research.references.map((r, i) => (
                      <a key={i} href={r.url} target="_blank" rel="noreferrer"
                        className="group block border border-border p-3 hover:border-accent/60 hover:bg-secondary/40 transition-colors">
                        <div className="flex items-start justify-between gap-2">
                          <span className="font-serif text-base font-semibold text-foreground group-hover:text-accent">{r.title}</span>
                          {r.verified === false
                            ? <SearchIcon className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-1" title={`Opens a search of ${r.publisher || "the web"} — direct link not verified`} />
                            : <ExternalLink className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-1" />}
                        </div>
                        {r.publisher && <span className="font-mono text-[10px] uppercase tracking-wider text-accent">{r.publisher}</span>}
                        {r.blurb && <p className="mt-1 text-xs text-muted-foreground leading-relaxed">{r.blurb}</p>}
                      </a>
                    ))}
                  </div>
                </div>

                {/* Videos */}
                <div>
                  <p className="font-mono text-xs uppercase tracking-[0.25em] text-accent mb-3 flex items-center gap-2">
                    <Youtube className="h-3.5 w-3.5" /> Video Research
                  </p>
                  <div className="space-y-2" data-testid="research-videos">
                    {research.videos.map((v, i) => (
                      <a key={i} href={v.url} target="_blank" rel="noreferrer"
                        className="group flex items-center gap-3 border border-border p-3 hover:border-accent/60 hover:bg-secondary/40 transition-colors">
                        <span className="flex items-center justify-center h-9 w-9 bg-red-600/10 text-red-600 shrink-0">
                          <Youtube className="h-4 w-4" />
                        </span>
                        <span className="min-w-0">
                          <span className="block font-serif text-base font-semibold text-foreground group-hover:text-accent truncate">{v.title}</span>
                          <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{v.channel || "Search on YouTube"}</span>
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              </div>

              {/* Quick search links */}
              {research.search && (
                <div className="pt-2 border-t border-border">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5"><SearchIcon className="h-3 w-3" /> Quick searches</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(research.search).map(([k, url]) => (
                      <a key={k} href={url} target="_blank" rel="noreferrer"
                        className="font-mono text-[11px] uppercase tracking-wider border border-border px-3 py-1.5 text-muted-foreground hover:text-foreground hover:border-foreground/40 transition-colors">
                        {k}
                      </a>
                    ))}
                  </div>
                </div>
              )}
              {research.generated_at && (
                <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground/70">
                  AI-generated · verify sources independently
                </p>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Sources footnote */}
      {sources.length > 0 && (
        <section className="mx-auto max-w-6xl px-4 sm:px-8 lg:px-12 pb-12" data-testid="site-sources">
          <div className="border-t border-border pt-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-2.5 flex items-center gap-1.5">
              <Library className="h-3 w-3" /> Sources
            </p>
            <div className="flex flex-wrap gap-2">
              {sources.map((s) => (
                <a
                  key={s.url}
                  href={s.url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`source-link-${s.kind}`}
                  className="group inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider border border-border px-3 py-1.5 text-muted-foreground hover:text-accent hover:border-accent/60 transition-colors"
                >
                  <ExternalLink className="h-3 w-3 opacity-60 group-hover:opacity-100" />
                  {s.label}
                </a>
              ))}
            </div>
            <p className="mt-2.5 font-mono text-[10px] tracking-wide text-muted-foreground/70 normal-case">
              Reference pages for further reading. Verify claims independently.
            </p>
          </div>
        </section>
      )}

      <NearbySuggestions slug={p.slug} />

      <Dialog open={lightbox !== null} onOpenChange={() => setLightbox(null)}>
        <DialogContent className="max-w-4xl p-0 border-border bg-black overflow-hidden z-[3000]">
          <DialogTitle className="sr-only">{p.name} — photo viewer</DialogTitle>
          <DialogDescription className="sr-only">Gallery image viewer for {p.name}. Use the arrow controls to browse photos.</DialogDescription>
          {lightbox !== null && p.gallery?.[lightbox] && (
            <div className="relative">
              <img src={p.gallery[lightbox]} alt={commonsInfo(p.gallery[lightbox]).title || `${p.name} ${lightbox + 1}`} className="w-full h-auto object-contain max-h-[80vh]" />
              {p.gallery.length > 1 && (
                <>
                  <button
                    data-testid="lightbox-prev"
                    onClick={(e) => { e.stopPropagation(); setLightbox((lightbox - 1 + p.gallery.length) % p.gallery.length); }}
                    className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center justify-center h-11 w-11 bg-black/60 text-white hover:bg-black/80 transition-colors"
                    aria-label="Previous photo"
                  >
                    <ChevronLeft className="h-6 w-6" />
                  </button>
                  <button
                    data-testid="lightbox-next"
                    onClick={(e) => { e.stopPropagation(); setLightbox((lightbox + 1) % p.gallery.length); }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center h-11 w-11 bg-black/60 text-white hover:bg-black/80 transition-colors"
                    aria-label="Next photo"
                  >
                    <ChevronRight className="h-6 w-6" />
                  </button>
                </>
              )}
              {(() => {
                const info = commonsInfo(p.gallery[lightbox]);
                return (
                  <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/90 via-black/55 to-transparent px-4 pb-3 pt-12 flex items-end justify-between gap-4">
                    <div className="min-w-0">
                      <p data-testid="lightbox-caption" className="text-white text-sm font-serif leading-snug line-clamp-2">{info.title || p.name}</p>
                      {info.isCommons && (
                        <a href={info.source} target="_blank" rel="noreferrer" data-testid="lightbox-credit" className="mt-0.5 inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-amber-300 hover:underline">
                          <ExternalLink className="h-3 w-3" /> Wikimedia Commons · credit &amp; license
                        </a>
                      )}
                    </div>
                    {p.gallery.length > 1 && (
                      <span className="shrink-0 font-mono text-[11px] tracking-widest text-white/85">{lightbox + 1} / {p.gallery.length}</span>
                    )}
                  </div>
                );
              })()}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
